/**
 * Motivos prontos de pausa e cancelamento (só quem edita os manuais): os
 * botões que aparecem ao pausar ou cancelar uma execução. Config da unidade
 * com tela própria (regra 3 do CLAUDE.md); o padrão de unidade nova está em
 * `area_pcp/seeds.py` do backend.
 */
import React, { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { listarMotivos, salvarMotivos } from "../../services/areaPcp";
import { SetasDeOrdem } from "./componentes/ListaOrdenavel";
import { useAreaPcp } from "./offline/AreaPcpContext";
import { Aviso, BotaoSecundario, Secao } from "./styles";

const TIPOS = [
  { id: "pausa", rotulo: "Motivos de pausa", dica: "Opcional ao pausar; a pessoa também pode escrever." },
  { id: "cancelamento", rotulo: "Motivos de cancelamento", dica: "Cancelar sempre pede um motivo." },
];

const LinhaMotivo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  input[type="text"] {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 34px;
    padding: 0 10px;
    border-radius: 10px;
    border: 1px solid var(--border-strong);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 14px;
  }

  label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--ink-2);
    white-space: nowrap;
  }
`;

function mover(lista, de, para) {
  const copia = [...lista];
  const [item] = copia.splice(de, 1);
  copia.splice(para, 0, item);
  return copia;
}

function ListaDeMotivos({ tipo, itens, onMudar }) {
  return (
    <Secao>
      <h2>{tipo.rotulo}</h2>
      <p style={{ margin: 0, fontSize: 12.5, color: "var(--ink-3)" }}>{tipo.dica}</p>
      {itens.map((item, i) => (
        <LinhaMotivo key={item.chave}>
          <input
            type="text"
            value={item.texto}
            aria-label={`Motivo ${i + 1}`}
            maxLength={200}
            onChange={(e) => onMudar(itens.map((m, j) => (j === i ? { ...m, texto: e.target.value } : m)))}
          />
          <label>
            <input
              type="checkbox"
              checked={item.ativo}
              onChange={(e) => onMudar(itens.map((m, j) => (j === i ? { ...m, ativo: e.target.checked } : m)))}
            />
            ativo
          </label>
          <SetasDeOrdem indice={i} total={itens.length} rotulo={item.texto || "motivo"} onMover={(de, para) => onMudar(mover(itens, de, para))} />
          <BotaoSecundario
            type="button"
            aria-label={`Remover ${item.texto || "motivo"}`}
            onClick={() => onMudar(itens.filter((_, j) => j !== i))}
            style={{ width: 34, padding: 0 }}
          >
            <FiTrash2 size={15} aria-hidden="true" />
          </BotaoSecundario>
        </LinhaMotivo>
      ))}
      <BotaoSecundario
        type="button"
        style={{ alignSelf: "flex-start" }}
        onClick={() => onMudar([...itens, { chave: `novo-${Date.now()}`, texto: "", ativo: true }])}
      >
        <FiPlus size={15} aria-hidden="true" />
        Adicionar motivo
      </BotaoSecundario>
    </Secao>
  );
}

export default function MotivosPainel({ aberto, onFechar }) {
  const { request } = useAuth();
  const { carregarPacote } = useAreaPcp();
  const [listas, setListas] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!aberto) return;
    setErro("");
    listarMotivos(request)
      .then((motivos) => setListas(Object.fromEntries(
        TIPOS.map(({ id }) => [id, (motivos[id] || []).map((m) => ({ chave: m.id, texto: m.texto, ativo: m.ativo }))]),
      )))
      .catch((falha) => setErro(falha.message));
  }, [aberto, request]);

  const salvar = async () => {
    for (const { id } of TIPOS) {
      // eslint-disable-next-line no-await-in-loop
      await salvarMotivos(request, id, listas[id].map(({ texto, ativo }) => ({ texto, ativo })));
    }
    carregarPacote();
    onFechar();
  };

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Motivos prontos"
      subtitle="Aparecem como botões ao pausar ou cancelar uma execução."
      footer={
        <AsyncButton onClick={salvar} successMessage="Motivos salvos" disabled={!listas}>
          Salvar motivos
        </AsyncButton>
      }
    >
      {erro ? <Aviso $tom="danger">{erro}</Aviso> : null}
      {listas
        ? TIPOS.map((tipo) => (
          <ListaDeMotivos
            key={tipo.id}
            tipo={tipo}
            itens={listas[tipo.id]}
            onMudar={(itens) => setListas((atual) => ({ ...atual, [tipo.id]: itens }))}
          />
        ))
        : null}
    </SlideOverPanel>
  );
}
